import type * as React from 'react';

import { Icon } from '@gitbook/spine-icons';
import {
    Alert,
    Button,
    Card,
    CardActions,
    CardBody,
    Description,
    HoverCard,
    HoverCardRoot,
    HoverCardTrigger,
} from '@gitbook/spine-react';

import { useNavigation } from '../../../navigation';
import { useSiteContext } from '../../../state';

/**
 * Shared description component for not allowed links
 */
export function InvalidSpaceLinkDescription(): React.ReactElement {
    return (
        <Description>
            <p>
                This link references a space that isn't included in your site structure. The link
                will be broken on your published site.
            </p>
            <p>
                To fix this, either add the space to your site structure or use an absolute URL if
                it belongs to a different site.
            </p>
        </Description>
    );
}

/**
 * Warning icon with hover card indicating that a space is not part of the site.
 */
export function InvalidSpaceLinkHoverCard(): React.ReactElement {
    const href = useManageStructureHref();
    return (
        <HoverCardRoot>
            <HoverCardTrigger>
                <div className="inline-flex shrink-0 text-warning">
                    <Icon.Warning size={20} />
                </div>
            </HoverCardTrigger>
            <HoverCard>
                <Card className="max-w-80">
                    <CardBody>
                        <InvalidSpaceLinkDescription />
                        <CardActions>
                            <Button
                                label="Manage site structure"
                                kind="primary"
                                size="small"
                                href={href}
                                target="_blank"
                            />
                        </CardActions>
                    </CardBody>
                </Card>
            </HoverCard>
        </HoverCardRoot>
    );
}

/**
 * Alert component indicating that a space is not part of the site.
 */
export function InvalidSpaceLinkAlert(): React.ReactElement {
    const href = useManageStructureHref();

    return (
        <Alert
            className="mt-4"
            kind="warning"
            title="Space not in site structure"
            details={
                <div className="flex flex-col gap-4">
                    <InvalidSpaceLinkDescription />
                    <Button
                        target="_blank"
                        label="Manage site structure"
                        kind="warning"
                        size="small"
                        href={href}
                        className="self-start"
                    />
                </div>
            }
        />
    );
}

function useManageStructureHref() {
    const { getNavigationURL } = useNavigation();
    const { site, organizationId } = useSiteContext();

    return getNavigationURL({
        screen: {
            type: 'site',
            organization: organizationId,
            site: site.id,
            tab: { type: 'settings', section: 'structure' },
        },
    });
}
